<template>
  <VRow
    class="d-flex pb-8"
    :class="{'flex-column': $vuetify.breakpoint.smAndDown, 'pl-6': $vuetify.breakpoint.smAndUp }"
    data-test="payment-selection-container"
  >
    <VCol
      lg="3"
      md="3"
      sm="12"
      xs="12"
      class="d-flex align-items-center"
      :class="{'flex-column': $vuetify.breakpoint.mdAndUp}"
    >
      <div :class="{'w-100': $vuetify.breakpoint.mdAndUp}">
        <VIcon
          large
          class="checkout-icon"
          :class="{'pr-1': $vuetify.breakpoint.smAndDown }"
        >
          mdi-wallet
        </VIcon>
      </div>
      <div :class="{'w-100': $vuetify.breakpoint.mdAndUp}">
        <span class="body-bold" data-test="payment-title"><span class="error--text" aria-hidden="true">* </span>{{ $t('checkout.payment') }}</span>
      </div>
    </VCol>
    <VCol
      cols="12"
      md="8"
      sm="11"
      class="my-0 py-0"
    >
      <ValidationProvider
        ref="payment"
        v-slot="{ ariaInput }"
        name="payment"
        :rules="{ required: { allowFalse: false } }"
        mode="eager"
      >
        <VRadioGroup
          v-bind="ariaInput"
          :value="paymentType"
          class="my-0 pt-3 d-flex"
          hideDetails
          :row="$vuetify.breakpoint.xs"
          :column="$vuetify.breakpoint.xs"
          @change="updatePaymentType"
        >
            <VRadio
              data-test="onlinePaymentButton"
              :class="{ 'pb-4': $vuetify.breakpoint.xs }"
              :value="preAuthorization"
            >
              <template #label>
                <div class="d-flex flex-column pr-0">
                  <span class="body-1 textPrimary--text" data-test="online-payment-label">{{ $t('checkout.onlinePayment') }}</span>
                  <span class="caption" data-test="online-payment-description">{{ $t('checkout.descriptionOnlinePayment') }}</span>
                </div>
              </template>
            </VRadio>
            <VRadio
              v-if="isOnBoardPaymentAllowed"
              data-test="onboardPaymentButton"
              :value="onBoard"
              class="m-0"
            >
              <template #label>
                <div class="d-flex flex-column pr-0">
                  <span class="body-1 textPrimary--text" data-test="onboard-payment-label">{{ $t('checkout.onboardPayment')}}</span>
                  <span class="caption" data-test="onboard-payment-description">{{ $t('checkout.descriptionOnboardPayment') }}</span>
                </div>
              </template>
            </VRadio>
        </VRadioGroup>
      </ValidationProvider>
      <div :class="$vuetify.breakpoint.xs ? 'w-100' : 'w-50'">
        <VSelect
          v-if="getIsPaymentMethodsDropdownShown"
          ref="autocomplete"
          v-model="selectedPaymentMethod"
          data-test="paymentSelectionDropdown"
          item-text="name"
          class="py-0"
          attach
          disable-lookup
          no-filter
          hideDetails
          return-object
          :menu-props="{ closeOnContentClick: true }"
          :items="paymentMethods"
          :label="$t('checkout.paymentMethodLabel')"
          @change="(value) => changePaymentMethod(value)"
        />
        <span 
          v-if="getIsPaymentMethodsDropdownShown && !selectedPaymentMethod.paymentMethodId"
          class="caption textSecondary--text"
          data-test="new-payment-method-hint"
        >
          {{ this.$t('checkout.newPaymentMethodHint') }}
        </span>
      </div>
    </VCol>
  </VRow>
</template>

<script lang="ts">
export default {
  name: 'PaymentSelection',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import PaymentTypeEnum from '@slg/web-customer-shared/src/enum/PaymentTypeEnum';
import UserService from '@slg/web-customer-shared/src/services/UserService';
import { useI18n } from '../../../../composables';

const props = defineProps({
  paymentType: {
    type: String,
    default: '',
  },
  paymentMethod: {
    type: String,
    default: '',
  },
  isOnBoardPaymentAllowed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['updatePaymentType', 'updatePaymentMethod']);

const { t } = useI18n();

const paymentMethods = ref([]);
const selectedPaymentMethod = ref({
  name: '',
  paymentMethodId: '',
});
const prePaid = PaymentTypeEnum.PRE_PAID;
const onBoard = PaymentTypeEnum.ON_BOARD;
const preAuthorization = PaymentTypeEnum.PRE_AUTHORIZATION;

const getIsPaymentMethodsDropdownShown = computed(() => paymentMethods.value.length && props.paymentType === PaymentTypeEnum.PRE_AUTHORIZATION);

async function loadPaymentMethods() {
  const { data: { preAuthorisationMethods } } = await UserService.getPaymentMethods();

  paymentMethods.value = preAuthorisationMethods?.map((pm) => ({
    name: `${pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)} ${t('checkout.endingIn')} ${pm.last4}`,
    paymentMethodId: pm.id,
  }));

  paymentMethods.value.push({
    name: t('checkout.newPaymentMethod'),
    paymentMethodId: null,
  });

  if (props.paymentMethod) {
    selectedPaymentMethod.value = paymentMethods.value?.find((pm) => pm.paymentMethodId === props.paymentMethod);
  } else {
    selectedPaymentMethod.value = paymentMethods.value[0];
    emit('updatePaymentMethod', paymentMethods.value[0].paymentMethodId);
  }
}

loadPaymentMethods();

function updatePaymentType(newValue) {
  emit('updatePaymentType', newValue);
}

function changePaymentMethod(value) {
  emit('updatePaymentMethod', value.paymentMethodId);
}
</script>
<style lang="scss" scoped>
::v-deep .v-menu__content {
  @media screen and (min-width: 600px) {
    min-width: 250px !important;
  }
}
</style>
